import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const IdleTimerContext = createContext();

export const IdleTimerProvider = ({ children }) => {
  const [idleTime, setIdleTime] = useState(0);
  const [idleThreshold, setIdleThreshold] = useState(Infinity);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isLastPhaseCompleted, setIsLastPhaseCompleted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getBaseType = type => (/\d+$/.test(type) ? type.replace(/\d+$/, '') : type);

  const resetIdleTimer = useCallback(() => {
    setIdleTime(0);
    setShouldRedirect(false);
  }, []);

  const getRedirectPath = useCallback(() => {
    const [station, rawType] = location.pathname.split('/').slice(1, 3);
    const type = getBaseType(rawType);

    const redirectPaths = {
      startseite: `/${station}`,
      taste: `/${station}/startseite`,
    };

    return redirectPaths[type] || '/';
  }, [location.pathname]);

  useEffect(() => {
    if (idleThreshold === Infinity) return;

    const remainingTime = idleThreshold * 1000 - idleTime * 1000;
    const timer = setTimeout(() => setShouldRedirect(true), remainingTime);

    return () => clearTimeout(timer);
  }, [idleTime, idleThreshold]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate(getRedirectPath());
      resetIdleTimer();
    }
  }, [shouldRedirect, navigate, getRedirectPath, resetIdleTimer]);

  useEffect(() => {
    const thresholds = {
      screensaver: Infinity,
      startseite: 60,
      taste: isLastPhaseCompleted ? 25 : Infinity,
    };

    const type = location.pathname.split('/')[2];
    const baseType = getBaseType(type);
    const newThreshold = thresholds[baseType] || Infinity;

    if (newThreshold !== idleThreshold) {
      setIdleThreshold(newThreshold);
      resetIdleTimer();
    }
  }, [location.pathname, isLastPhaseCompleted, idleThreshold, resetIdleTimer]);

  return (
    <IdleTimerContext.Provider
      value={{ resetIdleTimer, setIdleThreshold, setIsLastPhaseCompleted }}
    >
      {children}
    </IdleTimerContext.Provider>
  );
};

export const useIdleTimer = () => useContext(IdleTimerContext);
